import Box from "@mui/material/Box";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { checkEmailApi as checkEmailApi } from "../api/register";
import { notify } from "../pages/public/components/Toast";

interface RegisterEmailTextFieldType extends Omit<TextFieldProps, ""> {
  isDomainError: boolean;
  isDotComError: boolean;
  isExistEmail: boolean;
  isEmail: boolean;
}

interface HelperTextType {
  isDomainError: boolean;
  isDotComError: boolean;
  isFocus: boolean;
  error?: boolean;
  isValue?: boolean;
}

function HelperText({
  isDomainError,
  isDotComError,
  error,
  isValue,
  isFocus,
}: HelperTextType): React.JSX.Element | string {
  const isSuccess = !isValue ? "common.black" : "success.main";
  if (error) {
    return (
      <>
        <Typography
          color={isDomainError || isDotComError ? "error.main" : isSuccess}
          component="div"
          variant="caption"
        >
          Este não é um e-mail válido.
        </Typography>
        <Box component="ul" sx={{ m: "unset" }}>
          <Typography
            color={isDomainError ? "error.main" : isSuccess}
            component="li"
            variant="caption"
          >
            @gmail; @hotmail; outros.
          </Typography>
          <Typography
            color={isDotComError ? "error.main" : isSuccess}
            component="li"
            variant="caption"
          >
            .com
          </Typography>
        </Box>
      </>
    );
  }
  if (isFocus && !isValue) {
    return (
      <Typography color="common.black" component="div" variant="caption">
        Escolha um endereço de e-mail de fácil acesso por você.
      </Typography>
    );
  }
  return "";
}

export default function RegisterEmailTextField({
  isDomainError,
  isDotComError,
  isExistEmail,
  isEmail,
  ...props
}: RegisterEmailTextFieldType): React.JSX.Element {
  const [isFocus, setIsFocus] = useState(false);
  const { setError } = useFormContext();

  const checkEmail = async (email: string): Promise<void> => {
    try {
      const checkEmailExists = await checkEmailApi(email);
  
      if (checkEmailExists.exists) {
        setError("email", {
          message: "E-mail já está em uso no Portal.",
          type: "existingEmail",
        });
      }
    } catch (error) {
      const message = "Serviço indisponível, tente novamente mais tarde.";
      setError("email", {
        message,
        type: "existingEmail",
      });
      notify(message, "info", "");
    }
  };

  return (
    <TextField
      {...props}
      color="primary"
      helperText={
        isExistEmail || isEmail || !props.error ? (
          props.helperText
        ) : (
          <HelperText
            error={props.error}
            isDomainError={isDomainError}
            isDotComError={isDotComError}
            isFocus={isFocus}
            isValue={Boolean(props.value)}
          />
        )
      }
      onBlur={(event) => {
        setIsFocus(false);
        if (!props.error && props.value) {
          void checkEmail(String(props.value));
        }
        props.onBlur && props.onBlur(event);
      }}
      onFocus={(event) => {
        setIsFocus(true);
        props.onFocus && props.onFocus(event);
      }}
      variant="outlined"
    />
  );
}
